import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Clock, TrendingUp, ArrowRight, Heart } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function formatTimeLeft(endStr) {
  if (!endStr) return '';
  const end = new Date(endStr);
  const now = new Date();
  const diff = end - now;
  if (diff <= 0) return 'Ended';
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  return `${hours}h ${mins}m`;
}

export default function DomainCard({ domain, watchlistIds = [], onWatchlistChange }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isAuction = domain.is_auction;
  const timeLeft = isAuction ? formatTimeLeft(domain.auction_end) : null;
  const isWatchlisted = watchlistIds.includes(domain.id);

  const toggleWatchlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    try {
      if (isWatchlisted) {
        await axios.delete(`${API}/api/watchlist/${domain.id}`);
      } else {
        await axios.post(`${API}/api/watchlist/${domain.id}`);
      }
      if (onWatchlistChange) onWatchlistChange();
    } catch {}
  };

  return (
    <Link to={`/domains/${domain.id}`} data-testid={`domain-card-${domain.id}`}
      className="block bg-[#0A0A0A] border border-white/5 hover:border-[#00E5FF]/30 transition-all duration-300 rounded-sm overflow-hidden group relative">
      {/* Top Bar */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline" className="text-[10px] uppercase tracking-widest border-zinc-700 text-zinc-500">
            {domain.category}
          </Badge>
          {isAuction && timeLeft && (
            <div className="flex items-center gap-1 text-[10px] text-amber-400">
              <Clock className="h-3 w-3" /> {timeLeft}
            </div>
          )}
        </div>

        {/* Domain Name */}
        <h3 className="text-lg font-bold text-white group-hover:text-[#00E5FF] transition-colors font-['Outfit']">
          {domain.name}<span className="text-[#00E5FF]">{domain.tld}</span>
        </h3>

        {domain.description && (
          <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{domain.description}</p>
        )}
      </div>

      {/* Bottom */}
      <div className="px-4 py-3 border-t border-white/5 flex items-center justify-between">
        <div>
          {isAuction ? (
            <div>
              <span className="text-[10px] text-zinc-600 uppercase tracking-wider">{t('domains.currentBid')}</span>
              <div className="text-lg font-bold text-white flex items-center gap-1">
                <TrendingUp className="h-3.5 w-3.5 text-[#00E5FF]" />
                ${(domain.current_bid || domain.min_bid || 0).toLocaleString()}
              </div>
            </div>
          ) : (
            <div>
              <span className="text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.price')}</span>
              <div className="text-lg font-bold text-white">${domain.price?.toLocaleString()}</div>
            </div>
          )}
        </div>
        <div className="w-8 h-8 rounded-sm bg-[#00E5FF]/10 flex items-center justify-center group-hover:bg-[#00E5FF] transition-colors">
          <ArrowRight className="h-4 w-4 text-[#00E5FF] group-hover:text-black transition-colors" />
        </div>
      </div>

      {/* Featured Badge */}
      {domain.is_featured && (
        <div className="absolute top-0 right-0 px-2 py-0.5 bg-[#00E5FF] text-black text-[9px] font-bold uppercase tracking-wider">
          Featured
        </div>
      )}

      {/* Watchlist Heart */}
      {user && (
        <button onClick={toggleWatchlist}
          className={`absolute top-2 ${domain.is_featured ? 'left-2' : 'right-2'} p-1.5 rounded-sm transition-all ${isWatchlisted ? 'text-[#FF6B9D]' : 'text-zinc-600 hover:text-[#FF6B9D] opacity-0 group-hover:opacity-100'}`}
          data-testid={`watchlist-btn-${domain.id}`}>
          <Heart className={`h-4 w-4 ${isWatchlisted ? 'fill-current' : ''}`} />
        </button>
      )}
    </Link>
  );
}
